import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { baseApiURL } from "../../baseUrl";

const RouteAllocations = () => {
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [allocations, setAllocations] = useState(null);
  const [seatMap, setSeatMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAllocations, setLoadingAllocations] = useState(false);
  const [loadingSeatMap, setLoadingSeatMap] = useState(false);
  const [activeTab, setActiveTab] = useState("allocations"); // "allocations" or "seats"
  const [showSeatConfig, setShowSeatConfig] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedSeat, setSelectedSeat] = useState(null);
  const [seatConfig, setSeatConfig] = useState({
    seatsPerRow: 3,
    aislePosition: 2,
    frontRowsForStaff: 1,
    maleSectionStart: 2,
    maleSectionEnd: 0,
    femaleSectionStart: 0,
    femaleSectionEnd: 0
  });

  const fetchRouteSummaries = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseApiURL()}/transport/routes/summaries`);
      if (res.data.success) {
        setRoutes(res.data.routes || []);
      }
    } catch (error) {
      console.error(error);
      toast.error("Unable to load routes data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchRouteAllocations = async (routeId) => {
    try {
      setLoadingAllocations(true);
      const route = routes.find(r => r._id === routeId);
      setSelectedRoute(route);
      
      const res = await axios.get(`${baseApiURL()}/transport/routes/${routeId}/allocations`);
      if (res.data.success) {
        setAllocations(res.data);
      }
    } catch (error) {
      console.error(error);
      toast.error("Unable to load allocations data.");
    } finally {
      setLoadingAllocations(false);
    }
  };

  const fetchSeatMap = async (routeId) => {
    try {
      setLoadingSeatMap(true);
      const res = await axios.get(`${baseApiURL()}/transport/routes/${routeId}/seatmap`);
      if (res.data.success) {
        setSeatMap(res.data);
        setSeatConfig(res.data.seatConfig || {
          seatsPerRow: 3,
          aislePosition: 2,
          frontRowsForStaff: 1
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("Unable to load seat map.");
    } finally {
      setLoadingSeatMap(false);
    }
  };

  const handleRouteSelect = async (routeId) => {
    const route = routes.find(r => r._id === routeId);
    setSelectedRoute(route);
    await fetchRouteAllocations(routeId);
    if (activeTab === "seats") {
      await fetchSeatMap(routeId);
    }
  };

  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    if (tab === "seats" && selectedRoute) {
      await fetchSeatMap(selectedRoute._id);
    }
  };

  const handleSeatClick = (seat) => {
    if (seat.isOccupied) {
      setSelectedStudent(seat.allocation);
      setSelectedSeat(seat);
    } else if (!seat.isReserved) {
      setSelectedSeat(seat);
      setSelectedStudent(null);
    }
  };

  const assignSeatToStudent = async (enrollmentNo) => {
    if (!selectedSeat || !selectedRoute) {
      toast.error("Please select a seat first.");
      return;
    }

    try {
      const res = await axios.post(`${baseApiURL()}/transport/routes/assign-seat`, {
        routeId: selectedRoute._id,
        enrollmentNo,
        seatNumber: selectedSeat.seatNumber,
        seatType: selectedSeat.type
      });

      if (res.data.success) {
        toast.success(res.data.message);
        // Refresh data
        await fetchRouteAllocations(selectedRoute._id);
        await fetchSeatMap(selectedRoute._id);
        setSelectedStudent(null);
        setSelectedSeat(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to assign seat.");
    }
  };

  const removeSeatAssignment = async (enrollmentNo) => {
    if (!window.confirm("Remove seat assignment for this student?")) {
      return;
    }

    try {
      const res = await axios.post(`${baseApiURL()}/transport/routes/remove-seat`, {
        routeId: selectedRoute._id,
        enrollmentNo
      });

      if (res.data.success) {
        toast.success(res.data.message);
        // Refresh data
        await fetchRouteAllocations(selectedRoute._id);
        await fetchSeatMap(selectedRoute._id);
        setSelectedStudent(null);
        setSelectedSeat(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to remove seat assignment.");
    }
  };

  const autoAssignSeats = async () => {
    if (!window.confirm("Auto-assign seats to all unassigned students? This will assign seats based on gender sections.")) {
      return;
    }

    try {
      const res = await axios.post(`${baseApiURL()}/transport/routes/${selectedRoute._id}/auto-assign`);
      if (res.data.success) {
        toast.success(res.data.message);
        // Refresh data
        await fetchRouteAllocations(selectedRoute._id);
        await fetchSeatMap(selectedRoute._id);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to auto-assign seats.");
    }
  };

  const updateSeatConfig = async () => {
    try {
      const res = await axios.put(`${baseApiURL()}/transport/routes/${selectedRoute._id}/seat-config`, seatConfig);
      if (res.data.success) {
        toast.success("Seat configuration updated.");
        await fetchSeatMap(selectedRoute._id);
        setShowSeatConfig(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update seat configuration.");
    }
  };

  useEffect(() => {
    fetchRouteSummaries();
  }, []);

  return (
    <div className="space-y-6">
      <div className="bento-header-banner flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Bus Allocations & Seat Management</h1>
          <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
            View students allocated to each bus, manage seat assignments, and track seat distribution by gender.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Routes List */}
        <div className="lg:col-span-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">All Buses</h2>
            <button
              onClick={fetchRouteSummaries}
              className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
            >
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="h-20 bg-slate-100 dark:bg-slate-800 rounded-xl"></div>
                </div>
              ))}
            </div>
          ) : routes.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-xs border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
              No routes available.
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
              {routes.map(route => (
                <div
                  key={route._id}
                  onClick={() => handleRouteSelect(route._id)}
                  className={`border rounded-xl p-3.5 cursor-pointer transition-all ${
                    selectedRoute?._id === route._id
                      ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/40'
                      : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50 hover:border-indigo-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-xs font-bold text-slate-900 dark:text-white">
                        {route.busNumber} • {route.routeName}
                      </h3>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">{route.busName}</p>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                      route.status === 'active' ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' :
                      route.status === 'maintenance' ? 'bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800' :
                      'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                    }`}>
                      {route.status}
                    </span>
                  </div>

                  {/* Capacity Bar */}
                  <div className="mb-2.5">
                    <div className="flex justify-between text-[11px] text-slate-500 dark:text-slate-400 font-semibold mb-1">
                      <span>Capacity: {route.allocatedSeats}/{route.capacity}</span>
                      <span>Left: {route.seatsLeft}</span>
                    </div>
                    <div className="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-2">
                      <div
                        className="bg-indigo-600 dark:bg-indigo-500 h-2 rounded-full transition-all"
                        style={{ width: `${(route.allocatedSeats / route.capacity) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Gender Distribution */}
                  <div className="grid grid-cols-3 gap-1.5 text-xs">
                    <div className="text-center p-1.5 bg-blue-50/60 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-800/50 rounded-lg">
                      <div className="font-bold text-blue-700 dark:text-blue-300">{route.maleCount}</div>
                      <div className="text-[10px] text-blue-600 dark:text-blue-400">Boys</div>
                    </div>
                    <div className="text-center p-1.5 bg-pink-50/60 dark:bg-pink-950/40 border border-pink-100 dark:border-pink-800/50 rounded-lg">
                      <div className="font-bold text-pink-700 dark:text-pink-300">{route.femaleCount}</div>
                      <div className="text-[10px] text-pink-600 dark:text-pink-400">Girls</div>
                    </div>
                    <div className="text-center p-1.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
                      <div className="font-bold text-slate-700 dark:text-slate-300">{route.otherCount}</div>
                      <div className="text-[10px] text-slate-500 dark:text-slate-400">Other</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs p-6 space-y-6">
          {!selectedRoute ? (
            <div className="text-center py-12 text-slate-400">
              <div className="text-4xl mb-3">🚌</div>
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-1">Select a Bus</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Choose a bus from the list to view allocated students and manage seat assignments.</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h2 className="text-base font-bold text-slate-900 dark:text-white">
                    {selectedRoute.busNumber} - {selectedRoute.routeName}
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{selectedRoute.busName}</p>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => window.print()}
                    className="px-3.5 py-1.5 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                  >
                    Print List
                  </button>
                  
                  <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden flex">
                    <button
                      onClick={() => handleTabChange("allocations")}
                      className={`px-3.5 py-1.5 text-xs font-bold transition cursor-pointer ${
                        activeTab === "allocations"
                          ? "bg-indigo-600 text-white"
                          : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                      }`}
                    >
                      Allocations
                    </button>
                    <button
                      onClick={() => handleTabChange("seats")}
                      className={`px-3.5 py-1.5 text-xs font-bold transition cursor-pointer ${
                        activeTab === "seats"
                          ? "bg-indigo-600 text-white"
                          : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                      }`}
                    >
                      Seat Map
                    </button>
                  </div>
                </div>
              </div>

              {/* Tab Content */}
              {activeTab === "allocations" ? (
                loadingAllocations ? (
                  <div className="space-y-6">
                    <div className="animate-pulse">
                      <div className="h-6 bg-slate-200 dark:bg-slate-800 rounded w-1/3 mb-4"></div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        {[1, 2, 3].map(i => (
                          <div key={i} className="h-24 bg-slate-200 dark:bg-slate-800 rounded-xl"></div>
                        ))}
                      </div>
                      <div className="h-48 bg-slate-200 dark:bg-slate-800 rounded-xl"></div>
                    </div>
                  </div>
                ) : allocations ? (
                  <div className="space-y-6">
                    {/* Statistics Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="bg-blue-50/60 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-800/60 rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">Boys Allocated</div>
                            <div className="text-2xl font-black text-blue-700 dark:text-blue-300">
                              {allocations.statistics.male}
                            </div>
                          </div>
                          <div className="text-2xl">👨</div>
                        </div>
                        <div className="mt-1 text-xs text-blue-600 dark:text-blue-400 font-semibold">
                          Seats Left: {allocations.statistics.maleSeatsLeft}
                        </div>
                      </div>

                      <div className="bg-pink-50/60 dark:bg-pink-950/40 border border-pink-100 dark:border-pink-800/60 rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-[10px] font-bold text-pink-600 dark:text-pink-400 uppercase tracking-wider">Girls Allocated</div>
                            <div className="text-2xl font-black text-pink-700 dark:text-pink-300">
                              {allocations.statistics.female}
                            </div>
                          </div>
                          <div className="text-2xl">👩</div>
                        </div>
                        <div className="mt-1 text-xs text-pink-600 dark:text-pink-400 font-semibold">
                          Seats Left: {allocations.statistics.femaleSeatsLeft}
                        </div>
                      </div>

                      <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total Allocated</div>
                            <div className="text-2xl font-black text-slate-900 dark:text-white">
                              {allocations.statistics.totalAllocated}
                            </div>
                          </div>
                          <div className="text-2xl">👥</div>
                        </div>
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400 font-semibold">
                          Seats Left: {allocations.statistics.seatsLeft}
                        </div>
                      </div>
                    </div>

                    {/* Allocated Students */}
                    <div className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden bg-white dark:bg-slate-900">
                      <div className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-4">
                        <div className="flex items-center justify-between">
                          <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Allocated Students ({allocations.statistics.totalAllocated})</h3>
                          <button
                            onClick={autoAssignSeats}
                            className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl shadow-xs transition cursor-pointer"
                          >
                            Auto Assign Seats
                          </button>
                        </div>
                      </div>

                      {/* Boys Section */}
                      {allocations.allocations.male.length > 0 && (
                        <div className="p-4 border-b border-slate-200 dark:border-slate-800">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-blue-600">👨</span>
                            <h4 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Boys ({allocations.allocations.male.length})</h4>
                          </div>
                          <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
                              <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                                <tr>
                                  <th className="text-left p-3">Seat No.</th>
                                  <th className="text-left p-3">Enrollment No.</th>
                                  <th className="text-left p-3">Name</th>
                                  <th className="text-left p-3">Branch/Sem</th>
                                  <th className="text-left p-3">Stop</th>
                                  <th className="text-left p-3">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                                {allocations.allocations.male.map((student, idx) => (
                                  <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                                    <td className="p-3">
                                      {student.seatNumber ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                                          {student.seatNumber}
                                        </span>
                                      ) : (
                                        <span className="text-slate-400 text-[11px]">Not assigned</span>
                                      )}
                                    </td>
                                    <td className="p-3 font-mono font-bold">{student.enrollmentNo}</td>
                                    <td className="p-3 font-bold">{student.name}</td>
                                    <td className="p-3">{student.branch} • Sem {student.semester}</td>
                                    <td className="p-3">{student.stopName}</td>
                                    <td className="p-3">
                                      {student.seatNumber ? (
                                        <button
                                          onClick={() => removeSeatAssignment(student.enrollmentNo)}
                                          className="text-rose-600 dark:text-rose-400 hover:underline font-bold cursor-pointer"
                                        >
                                          Remove Seat
                                        </button>
                                      ) : (
                                        <button
                                          onClick={() => {
                                            setSelectedStudent(student);
                                            handleTabChange("seats");
                                          }}
                                          className="text-indigo-600 dark:text-indigo-400 hover:underline font-bold cursor-pointer"
                                        >
                                          Assign Seat
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Girls Section */}
                      {allocations.allocations.female.length > 0 && (
                        <div className="p-4 border-b border-slate-200 dark:border-slate-800">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-pink-600">👩</span>
                            <h4 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Girls ({allocations.allocations.female.length})</h4>
                          </div>
                          <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
                              <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                                <tr>
                                  <th className="text-left p-3">Seat No.</th>
                                  <th className="text-left p-3">Enrollment No.</th>
                                  <th className="text-left p-3">Name</th>
                                  <th className="text-left p-3">Branch/Sem</th>
                                  <th className="text-left p-3">Stop</th>
                                  <th className="text-left p-3">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                                {allocations.allocations.female.map((student, idx) => (
                                  <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                                    <td className="p-3">
                                      {student.seatNumber ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                                          {student.seatNumber}
                                        </span>
                                      ) : (
                                        <span className="text-slate-400 text-[11px]">Not assigned</span>
                                      )}
                                    </td>
                                    <td className="p-3 font-mono font-bold">{student.enrollmentNo}</td>
                                    <td className="p-3 font-bold">{student.name}</td>
                                    <td className="p-3">{student.branch} • Sem {student.semester}</td>
                                    <td className="p-3">{student.stopName}</td>
                                    <td className="p-3">
                                      {student.seatNumber ? (
                                        <button
                                          onClick={() => removeSeatAssignment(student.enrollmentNo)}
                                          className="text-rose-600 dark:text-rose-400 hover:underline font-bold cursor-pointer"
                                        >
                                          Remove Seat
                                        </button>
                                      ) : (
                                        <button
                                          onClick={() => {
                                            setSelectedStudent(student);
                                            handleTabChange("seats");
                                          }}
                                          className="text-indigo-600 dark:text-indigo-400 hover:underline font-bold cursor-pointer"
                                        >
                                          Assign Seat
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {allocations.statistics.totalAllocated === 0 && (
                        <div className="p-8 text-center text-slate-400">
                          <div className="text-4xl mb-2">👤</div>
                          <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200">No Students Allocated</h4>
                          <p className="text-[11px] text-slate-400 mt-0.5">This bus doesn't have any students allocated yet.</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null
              ) : (
                // Seat Map Tab
                <div className="space-y-6">
                  {/* Seat Map Header */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Seat Map</h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Click on seats to assign students. Green = Occupied, Blue = Available, Gray = Reserved
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowSeatConfig(!showSeatConfig)}
                        className="px-3.5 py-1.5 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                      >
                        {showSeatConfig ? "Hide Config" : "Seat Config"}
                      </button>
                      <button
                        onClick={autoAssignSeats}
                        className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl shadow-xs transition cursor-pointer"
                      >
                        Auto Assign
                      </button>
                    </div>
                  </div>

                  {/* Seat Configuration Modal */}
                  {showSeatConfig && (
                    <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 space-y-3">
                      <h4 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Seat Configuration</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Seats per Row</label>
                          <input
                            type="number"
                            min="1"
                            max="5"
                            value={seatConfig.seatsPerRow}
                            onChange={(e) => setSeatConfig({...seatConfig, seatsPerRow: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Aisle Position</label>
                          <input
                            type="number"
                            min="1"
                            max={seatConfig.seatsPerRow}
                            value={seatConfig.aislePosition}
                            onChange={(e) => setSeatConfig({...seatConfig, aislePosition: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Staff Rows</label>
                          <input
                            type="number"
                            min="0"
                            max="3"
                            value={seatConfig.frontRowsForStaff}
                            onChange={(e) => setSeatConfig({...seatConfig, frontRowsForStaff: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={updateSeatConfig}
                            className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl shadow-xs transition cursor-pointer"
                          >
                            Update Config
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Selected Student Info */}
                  {selectedStudent && (
                    <div className="bg-indigo-50/60 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800/60 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-xs font-bold text-indigo-900 dark:text-indigo-300 uppercase tracking-wider">Selected Student</h4>
                          <p className="text-xs text-indigo-700 dark:text-indigo-400 font-semibold mt-0.5">
                            {selectedStudent.name} ({selectedStudent.enrollmentNo})
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedSeat && (
                            <span className="text-xs text-indigo-700 dark:text-indigo-300 font-bold">
                              Selected Seat: <strong>{selectedSeat.seatNumber}</strong>
                            </span>
                          )}
                          <button
                            onClick={() => assignSeatToStudent(selectedStudent.enrollmentNo)}
                            className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl shadow-xs transition cursor-pointer"
                          >
                            Assign to Selected Seat
                          </button>
                          <button
                            onClick={() => setSelectedStudent(null)}
                            className="px-3.5 py-1.5 border border-slate-200 dark:border-slate-800 text-xs font-bold text-slate-700 dark:text-slate-300 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Seat Map Visualization */}
                  {loadingSeatMap ? (
                    <div className="animate-pulse">
                      <div className="h-64 bg-slate-200 dark:bg-slate-800 rounded-2xl"></div>
                    </div>
                  ) : seatMap ? (
                    <div className="space-y-6">
                      {/* Bus Layout */}
                      <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
                        {/* Driver Area */}
                        <div className="flex justify-center mb-6">
                          <div className="bg-slate-200 dark:bg-slate-800 px-5 py-1.5 rounded-xl border border-slate-300 dark:border-slate-700">
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-300">🚌 Driver & Staff Area</span>
                          </div>
                        </div>

                        {/* Seats Grid */}
                        <div className="space-y-3">
                          {seatMap.seatMap.map((row, rowIndex) => (
                            <div key={rowIndex} className="flex items-center gap-3">
                              <div className="w-14 text-xs font-bold text-slate-500 dark:text-slate-400">
                                Row {row.rowNumber}
                              </div>
                              <div className="flex-1 flex items-center justify-center gap-2.5">
                                {row.seats.map((seat, seatIndex) => (
                                  <button
                                    key={seatIndex}
                                    onClick={() => handleSeatClick(seat)}
                                    className={`
                                      w-14 h-14 rounded-xl flex flex-col items-center justify-center
                                      font-bold text-xs transition-all cursor-pointer border
                                      ${seat.isOccupied 
                                        ? 'bg-emerald-50 dark:bg-emerald-950/60 border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300' 
                                        : seat.isReserved
                                        ? 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400 cursor-not-allowed'
                                        : selectedSeat?.seatNumber === seat.seatNumber
                                        ? 'bg-indigo-50 dark:bg-indigo-950/60 border-indigo-400 text-indigo-700 dark:text-indigo-300'
                                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200 hover:border-indigo-400'
                                      }
                                    `}
                                    disabled={seat.isReserved}
                                  >
                                    <div>{seat.seatNumber}</div>
                                    {seat.isOccupied && (
                                      <div className="text-[9px] mt-0.5 truncate max-w-full px-1">
                                        {seat.allocation?.enrollmentNo}
                                      </div>
                                    )}
                                    {seat.section === 'male' && !seat.isOccupied && !seat.isReserved && (
                                      <div className="text-[10px] text-blue-600 dark:text-blue-400">♂</div>
                                    )}
                                    {seat.section === 'female' && !seat.isOccupied && !seat.isReserved && (
                                      <div className="text-[10px] text-pink-600 dark:text-pink-400">♀</div>
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Legend */}
                        <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-800">
                          <div className="flex flex-wrap gap-4 justify-center">
                            <div className="flex items-center gap-1.5">
                              <div className="w-3.5 h-3.5 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-300 dark:border-emerald-800 rounded"></div>
                              <span className="text-xs text-slate-500 dark:text-slate-400">Occupied</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <div className="w-3.5 h-3.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded"></div>
                              <span className="text-xs text-slate-500 dark:text-slate-400">Available</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <div className="w-3.5 h-3.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded"></div>
                              <span className="text-xs text-slate-500 dark:text-slate-400">Reserved</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <div className="w-3.5 h-3.5 bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-400 rounded"></div>
                              <span className="text-xs text-slate-500 dark:text-slate-400">Selected</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Statistics */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-3.5 text-center">
                          <div className="text-xl font-bold text-slate-900 dark:text-white">
                            {seatMap.statistics.occupiedSeats}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400 font-semibold">Occupied Seats</div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-3.5 text-center">
                          <div className="text-xl font-bold text-slate-900 dark:text-white">
                            {seatMap.statistics.availableSeats}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400 font-semibold">Available Seats</div>
                        </div>
                        <div className="bg-blue-50/60 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-800/60 rounded-xl p-3.5 text-center">
                          <div className="text-xl font-bold text-blue-700 dark:text-blue-300">
                            {seatMap.statistics.maleSeats}
                          </div>
                          <div className="text-xs text-blue-600 dark:text-blue-400 font-semibold">Male Section</div>
                        </div>
                        <div className="bg-pink-50/60 dark:bg-pink-950/40 border border-pink-100 dark:border-pink-800/60 rounded-xl p-3.5 text-center">
                          <div className="text-xl font-bold text-pink-700 dark:text-pink-300">
                            {seatMap.statistics.femaleSeats}
                          </div>
                          <div className="text-xs text-pink-600 dark:text-pink-400 font-semibold">Female Section</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-400 text-xs">
                      <div className="text-4xl mb-2">💺</div>
                      <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200">No Seat Map Available</h4>
                      <p className="text-[11px] text-slate-400 mt-0.5">Seat map data could not be loaded for this bus.</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RouteAllocations;